import { AlertTriangle, Info, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ArticleBlock, ArticleSampleTable } from '@/api/types';
import { ResultTable } from '@/components/editor/ResultTable';
import { EditorialContent } from '@/components/problems/EditorialTab';
import type { QueryResult } from '@/types';
import { SqlEditorBlock } from './SqlEditorBlock';

const CALLOUT = {
  info: { icon: Info, className: 'border-brand-500/30 bg-brand-500/10', iconClass: 'text-brand-600' },
  warning: { icon: AlertTriangle, className: 'border-[var(--medium)]/30 bg-[var(--medium)]/10', iconClass: 'text-[var(--medium)]' },
  tip: { icon: Lightbulb, className: 'border-[var(--easy)]/30 bg-[var(--easy)]/10', iconClass: 'text-[var(--easy)]' },
};

function SampleTables({ tables }: { tables?: ArticleSampleTable[] | null }) {
  if (!tables?.length) return null;
  return (
    <div className="mb-4 space-y-3">
      {tables.map((table) => (
        <div key={table.name}>
          <div className="mb-1 font-mono text-xs font-semibold text-content-secondary">{table.name}</div>
          <ResultTable result={table as QueryResult} />
        </div>
      ))}
    </div>
  );
}

/** Maqola bloklari. Hammasi prerender HTML ga tushadi; tayyor natijalar uchun server chaqirilmaydi. */
export function ArticleBlocks({ blocks, articleSlug }: { blocks: ArticleBlock[]; articleSlug: string }) {
  const { t } = useTranslation();
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading':
            return (
              <h2 key={block.id} id={block.id} className="mb-3 mt-8 scroll-mt-24 text-xl font-semibold">
                {block.text}
              </h2>
            );
          case 'markdown':
            return <EditorialContent key={index} contentMd={block.text} />;
          case 'callout': {
            const style = CALLOUT[block.variant] ?? CALLOUT.info;
            return (
              <aside key={index} className={`my-5 flex gap-3 rounded-lg border p-4 text-sm ${style.className}`}>
                <style.icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.iconClass}`} aria-hidden />
                <div className="min-w-0">
                  {block.title && <p className="mb-1 font-semibold">{block.title}</p>}
                  <p>{block.text}</p>
                </div>
              </aside>
            );
          }
          case 'sqlExample':
            return (
              <figure key={index} className="my-5">
                <SampleTables tables={block.tables} />
                <pre className="overflow-x-auto rounded-lg border border-line bg-surface p-4 font-mono text-sm">
                  <code>{block.query}</code>
                </pre>
                {block.result && (
                  <div className="mt-3">
                    <div className="mb-1 text-xs font-semibold text-content-secondary">{t('learn.resultLabel')}</div>
                    <ResultTable result={block.result as QueryResult} />
                  </div>
                )}
              </figure>
            );
          case 'sqlEditor':
            return (
              <div key={block.id}>
                <SampleTables tables={block.tables} />
                <SqlEditorBlock articleSlug={articleSlug} blockId={block.id} query={block.query} result={block.result} />
              </div>
            );
          case 'keyPoints':
            return (
              <section key={index} className="card my-6 p-5">
                <h3 className="mb-3 font-semibold">{t('learn.keyPoints')}</h3>
                <EditorialContent contentMd={block.items.map((item) => `- ${item}`).join('\n')} />
              </section>
            );
        }
      })}
    </>
  );
}
